import React, { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { SystemKey, SYSTEM_KEYS, SYSTEM_LABELS, TOAST_CONFIG } from '../../../../../constants';
import {
  updateModuleData,
  updateInverterData,
  updateBatteryData,
  updateProductionData,
  updateStringsData,
  setActiveSystem,
  setCROutput,
} from '../../../../../store/slices/createCRSlice';
import {
  selectModuleData,
  selectInverterData,
  selectBatteryData,
  selectProductionData,
  selectStringsData,
  selectIsModuleValid,
  selectIsInverterValid,
  selectIsOptimizerValid,
  selectSystemsValidation,
  selectStructure1Data,
  selectStructure2Data,
  selectStructure3Data
} from '../../../../../store/selectors/createCRSelectors';
import {
  selectModuleBrands,
  selectSolarModules,
  selectInverterBrands,
  selectInverters,
  selectOptimizers,
  selectBatteryBrands,
  selectBatteries,
  selectCombiners,
  selectRsds
} from '../../../../../store/selectors/databaseSelectors';
import { ModuleBrand, SolarModule, InverterBrand, InverterType, Optimizer } from '../../../../../types';
import { SearchDropdown } from '../../../../../components';
import { INVERTER_BRANDS, EQUIPMENT_DEFAULTS } from '../../../../../constants/app.constants';

const INVERTER_TYPE_OPTIONS = [
  { id: InverterType.INVERTER, label: 'Inverter' },
  { id: InverterType.MICROINVERTER, label: 'Microinverter' }
];

interface EquipmentStepProps {
  isModuleSelectorEnabled: boolean;
}

export const EquipmentStep: React.FC<EquipmentStepProps> = ({ isModuleSelectorEnabled }) => {
  const dispatch = useAppDispatch();
  const { is_initialized } = useAppSelector((state) => state.database);

  // Active system (local state, synced to Redux for selectors/reducers)
  const [activeSystem, setActiveSystemLocal] = useState<SystemKey>('system_one');

  // Reset to system_one on mount
  useEffect(() => {
    dispatch(setActiveSystem('system_one'));
  }, [dispatch]);

  // Redux selectors (automatically read from active system)
  const moduleData = useAppSelector(selectModuleData);
  const inverterData = useAppSelector(selectInverterData);
  const batteryData = useAppSelector(selectBatteryData);
  const productionData = useAppSelector(selectProductionData);
  const stringsData = useAppSelector(selectStringsData);
  const isModuleValid = useAppSelector(selectIsModuleValid);
  const isInverterValid = useAppSelector(selectIsInverterValid);
  const isOptimizerValid = useAppSelector(selectIsOptimizerValid);
  const systemsValidation = useAppSelector(selectSystemsValidation);
  const structure1Data = useAppSelector(selectStructure1Data);
  const structure2Data = useAppSelector(selectStructure2Data);
  const structure3Data = useAppSelector(selectStructure3Data);

  // Calculate total assigned panels across all structures
  const totalAssignedPanels = useMemo(() => {
    let total = 0;
    [structure1Data, structure2Data, structure3Data].forEach(structure => {
      if (structure.attachments_rails?.panel_rows_1?.rows) {
        total += structure.attachments_rails.panel_rows_1.rows.reduce((sum: number, row: any) => sum + (row.panel_count || 0), 0);
      }
      if (structure.attachments_rails?.panel_rows_2?.rows) {
        total += structure.attachments_rails.panel_rows_2.rows.reduce((sum: number, row: any) => sum + (row.panel_count || 0), 0);
      }
    });
    return total;
  }, [structure1Data, structure2Data, structure3Data]);

  // Database selectors
  const moduleBrands = useAppSelector(selectModuleBrands);
  const allModules = useAppSelector(selectSolarModules);
  const inverterBrands = useAppSelector(selectInverterBrands);
  const allInverters = useAppSelector(selectInverters);
  const optimizers = useAppSelector(selectOptimizers);
  const batteryBrands = useAppSelector(selectBatteryBrands);
  const allBatteries = useAppSelector(selectBatteries);
  const combiners = useAppSelector(selectCombiners);
  const rsds = useAppSelector(selectRsds);

  // Get modules, inverters, batteries based on selected brands using useMemo
  const modules = useMemo(() => {
    if (!moduleData.selected_brand) return [];
    return allModules
      .filter(module => module.brands.some(brand => brand.name === moduleData.selected_brand))
  }, [allModules, moduleData.selected_brand]);


  const batteries = useMemo(() => {
    if (!batteryData.selected_brand) return [];
    return allBatteries
      .filter(battery => battery.batteryBrand?.name === batteryData.selected_brand)
  }, [allBatteries, batteryData.selected_brand]);

  // Handler functions
  const handleModuleBrandSelect = (brand: ModuleBrand | undefined) => {
    dispatch(updateModuleData({
      selected_brand: brand?.name,
      selected_module: undefined,
      quantity: 0
    }));
  };

  const handleModuleSelect = (module: SolarModule | undefined) => {
    dispatch(updateModuleData({
      selected_module: module ? {
        fullName: module.fullName,
        wattage: module.wattage,
        modelNumber: module.modelNumber,
        width: module.width,
        height: module.height
      } : undefined,
      quantity: module ? 1 : 0
    }));
  };

  const handleModuleQuantityChange = (quantity: number) => {
    if (quantity < totalAssignedPanels) {
      toast.error(
        `Cannot reduce below ${totalAssignedPanels} panels (already assigned in panel rows)`,
        TOAST_CONFIG.toastOptions.error
      );
      return;
    }
    dispatch(updateModuleData({ quantity }));
  };

  const handleInverterTypeSelect = (type: InverterType) => {
    dispatch(updateInverterData({
      selected_type: type,
      selected_brand: undefined,
      selected_inverters: [],
      selected_optimizer: undefined,
      optimizer_quantity: 0,
      rsd: undefined,
      combiner: ''
    }));
  };

  const handleInverterBrandSelect = (brand: InverterBrand | undefined) => {
    const isTesla = brand?.name?.toUpperCase() === INVERTER_BRANDS.TESLA;
    dispatch(updateInverterData({
      selected_brand: brand ? {
        name: brand.name,
        inverter_type: brand.inverter_type
      } : undefined,
      selected_inverters: [],
      rsd: isTesla
        ? { type: EQUIPMENT_DEFAULTS.RSD_TYPE, quantity: 0, current: '' }
        : { type: '', quantity: 0, current: '' },
    }));
  };

  const handleInverterChange = (selectedInverters: any[]) => {
    const transformedInverters = selectedInverters.map(sel => {
      if (sel.inverter) {
        return {
          model: sel.inverter.model,
          wattage: sel.inverter.wattage,
          current: sel.inverter.current,
          quantity: sel.quantity
        };
      } else {
        return sel;
      }
    });
    dispatch(updateInverterData({ selected_inverters: transformedInverters }));
  };

  const handleOptimizerSelect = (optimizer: Optimizer | undefined) => {
    dispatch(updateInverterData({
      selected_optimizer: optimizer ? {
        type: optimizer.type,
        current: optimizer.current
      } : undefined
    }));
  };

  const handleOptimizerQuantityChange = (quantity: number) => {
    dispatch(updateInverterData({ optimizer_quantity: quantity }));
  };

  const handleRsdUpdate = (type: string, quantity: number, current: string) => {
    dispatch(updateInverterData({
      rsd: {
        type,
        quantity,
        current
      }
    }));
  };

  const handleCombinerUpdate = (combiner: string) => {
    dispatch(updateInverterData({ combiner }));
  };

  const handleSystemChange = (systemKey: SystemKey) => {
    setActiveSystemLocal(systemKey);
    dispatch(setActiveSystem(systemKey));
  };

  useEffect(() => {
    if (Object.keys(stringsData).length === 0) {
      dispatch(updateStringsData({ string_1: '' }));
    }
  }, [dispatch, stringsData]);

  const requiredStepsComplete = isModuleValid && isInverterValid && isOptimizerValid;

  return (
    <div className="flex-1 overflow-y-auto min-h-0 p-2">
      <div className="flex flex-col gap-4 p-1">
        <div className="flex gap-0 mb-2">
          {SYSTEM_KEYS.map((systemKey) => {
            const { isModuleValid: sysModuleValid, isInverterValid: sysInverterValid } = systemsValidation[systemKey];
            const isSystemConfigured = sysModuleValid && sysInverterValid;
            const isActive = activeSystem === systemKey;
            return (
              <button
                key={systemKey}
                onClick={() => handleSystemChange(systemKey)}
                className={`flex-1 pb-2 px-3 text-xs 3xl:text-sm font-medium tracking-wide transition-all duration-200 flex items-center justify-center gap-2 ${
                  isActive
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-slate-400 hover:text-slate-600 border-b-2 border-transparent'
                }`}
              >
                {SYSTEM_LABELS[systemKey]}
                {isSystemConfigured && (
                  <svg className={`w-3.5 h-3.5 ${isActive ? 'text-blue-500' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-4">
        {/* Modules Section */}
        <div className="rounded-2xl border border-slate-200/80 border-l-[3px] border-l-blue-400 bg-white overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 3xl:px-6 3xl:py-3.5 bg-blue-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[11px] 3xl:text-xs font-semibold text-blue-700">1</span>
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7h16M4 12h16M4 17h16" />
                </svg>
                <div>
                  <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Modules</span>
                  <span className="block text-[11px] 3xl:text-xs text-slate-500">Select brand, model, and panel quantity.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] 3xl:text-xs font-semibold uppercase tracking-wide text-blue-700">Required</span>
                <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                  isModuleValid ? 'border-emerald-200 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-slate-100 text-slate-400'
                }`}>
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="p-5">
              {totalAssignedPanels > 0 && (
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] 3xl:text-xs text-blue-800">
                  Minimum quantity is currently {totalAssignedPanels} based on assigned panel rows.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1.5">
                    <span className="text-red-500">*</span> Module Brand
                  </label>
                  <SearchDropdown
                    options={moduleBrands?.map(brand => ({ id: brand.id, label: brand.name })) || []}
                    onSelect={(option: any) => {
                      const brand = option ? moduleBrands?.find(b => b.id === option.id) : undefined;
                      if (!brand) {
                        dispatch(setCROutput({ generated_cr: {}, updated_cr: {} }));
                      }
                      handleModuleBrandSelect(brand);
                    }}
                    placeholder="Search brands..."
                    isLoading={!is_initialized}
                    value={moduleData.selected_brand ? (() => {
                      const brand = moduleBrands?.find(b => b.name === moduleData.selected_brand);
                      return brand ? { id: brand.id, label: brand.name } : null;
                    })() : null}
                    disabled={!isModuleSelectorEnabled}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`module-brand-${activeSystem}`}
                  />
                </div>

                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1.5">
                    <span className="text-red-500">*</span> Module Model
                  </label>
                  <SearchDropdown
                    options={modules?.map(module => ({ id: module.id, label: `${module.modelNumber} (${module.wattage}W)` })) || []}
                    onSelect={(option: any) => {
                      const module = option ? modules?.find(m => m.id === option.id) : undefined;
                      if (!module) {
                        dispatch(setCROutput({ generated_cr: {}, updated_cr: {} }));
                      }
                      handleModuleSelect(module);
                    }}
                    placeholder={moduleData.selected_brand ? "Search models..." : "Select brand first"}
                    isLoading={!is_initialized}
                    value={moduleData.selected_module ? (() => {
                      const module = modules?.find(m => m.modelNumber === moduleData.selected_module?.modelNumber);
                      return module ? { id: module.id, label: `${moduleData.selected_module.modelNumber} (${moduleData.selected_module.wattage}W)` } : null;
                    })() : null}
                    disabled={!isModuleSelectorEnabled || !moduleData.selected_brand}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`module-model-${activeSystem}`}
                  />
                </div>

                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1.5">
                    <span className="text-red-500">*</span> Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={moduleData.quantity || ''}
                    onChange={(e) => handleModuleQuantityChange(parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 text-xs 3xl:text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 focus:border-blue-300 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                    placeholder="0"
                    disabled={!isModuleSelectorEnabled}
                  />
                </div>
              </div>
            </div>
        </div>

        {/* Inverter Section */}
        <div className="bg-white rounded-2xl border border-slate-200/80 border-l-[3px] border-l-blue-400 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 3xl:px-6 3xl:py-3.5 bg-blue-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[11px] 3xl:text-xs font-semibold text-blue-700">2</span>
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <div>
                  <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Inverters</span>
                  <span className="block text-[11px] 3xl:text-xs text-slate-500">Choose type, brand, model mix, and required extras.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] 3xl:text-xs font-semibold uppercase tracking-wide text-blue-700">Required</span>
                <div className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                  isInverterValid && isOptimizerValid ? 'border-emerald-200 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-slate-100 text-slate-400'
                }`}>
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="p-5">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1.5">
                    <span className="text-red-500">*</span> Inverter Type
                  </label>
                  <SearchDropdown
                    options={INVERTER_TYPE_OPTIONS}
                    onSelect={(option: any) => {
                      if (!option) return;
                      const type = option.id === InverterType.MICROINVERTER ? InverterType.MICROINVERTER : InverterType.INVERTER;
                      handleInverterTypeSelect(type);
                    }}
                    placeholder="Select type..."
                    isLoading={false}
                    value={
                      inverterData.selected_type
                        ? {
                            id: inverterData.selected_type,
                            label: inverterData.selected_type === InverterType.MICROINVERTER ? 'Microinverter' : 'Inverter'
                          }
                        : null
                    }
                    disabled={!isModuleSelectorEnabled}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`inv-type-${activeSystem}`}
                  />
                </div>

                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1.5">
                    <span className="text-red-500">*</span> Inverter Brand
                  </label>
                  <SearchDropdown
                    options={inverterBrands?.filter(brand => brand.inverter_type === inverterData.selected_type)?.map(brand => ({ id: brand.id, label: brand.name })) || []}
                    onSelect={(option: any) => {
                      const filteredBrands = inverterBrands?.filter(brand => brand.inverter_type === inverterData.selected_type) || [];
                      const brand = option ? filteredBrands.find(b => b.id === option.id) : undefined;
                      handleInverterBrandSelect(brand);
                    }}
                    placeholder="Search brands..."
                    isLoading={!is_initialized}
                    value={inverterData.selected_brand ? { id: inverterData.selected_brand.name, label: inverterData.selected_brand.name } : null}
                    disabled={!isModuleSelectorEnabled || !inverterData.selected_type}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`brand-${inverterData.selected_type}-${activeSystem}`}
                  />
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-amber-100 bg-amber-50/40 p-3">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] 3xl:text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold">Models</span>
                      <div className="relative group">
                        <svg className="w-3.5 h-3.5 text-slate-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-52 rounded-lg bg-slate-800 px-3 py-2 text-[10px] 3xl:text-xs text-white shadow-lg">
                          <ul className="list-disc space-y-1 pl-3">
                            <li>Optimizer shows for SOLAREDGE</li>
                            <li>RSD shows for Tesla</li>
                            <li>Combiner shows for Microinverter</li>
                          </ul>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <span className="text-[10px] 3xl:text-xs text-amber-600">Add up to 3</span>
                </div>
                <div className="space-y-3">
                  <SearchDropdown
                    options={inverterData.selected_brand && allInverters ? allInverters?.filter(inv => inv.manufacturer.name === inverterData.selected_brand?.name)?.map(inv => ({
                      id: inv.id,
                      label: `${inv.model} (${inv.wattage}W)`
                    })) : []}
                    onSelect={(option: any) => {
                      if (option && (inverterData.selected_inverters || []).length < 3 && inverterData.selected_brand) {
                        const filteredInverters = allInverters?.filter(inv => inv.manufacturer.name === inverterData.selected_brand?.name) || [];
                        const inverter = filteredInverters.find(inv => inv.id === option.id);
                        if (inverter) {
                          const newSelection = {
                            id: `${inverter.id}-${Date.now()}`,
                            inverter,
                            quantity: 1
                          };
                          handleInverterChange([...(inverterData.selected_inverters || []), newSelection]);
                        }
                      }
                    }}
                    placeholder={
                      !inverterData.selected_brand
                        ? "Select brand first"
                        : "Search and add models..."
                    }
                    isLoading={!is_initialized}
                    value={null}
                    disabled={!isModuleSelectorEnabled || !inverterData.selected_brand || (inverterData.selected_inverters || []).length >= 3}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`${(inverterData.selected_inverters || []).length}-${activeSystem}`}
                  />

                  {(inverterData.selected_inverters || []).length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {(inverterData.selected_inverters || []).map((selection: any, index: number) => (
                        <div key={index} className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-lg w-full">
                          <div className="text-xs 3xl:text-sm font-medium text-slate-700 truncate flex-1 min-w-0">{selection.model}</div>
                          <div className="flex items-center justify-end gap-0">
                            <input
                              type="number"
                              min="1"
                              max="50"
                              value={selection.quantity}
                              onChange={(e) => {
                                const updatedSelections = [...(inverterData.selected_inverters || [])];
                                updatedSelections[index] = { ...updatedSelections[index], quantity: parseInt(e.target.value) || 1 };
                                dispatch(updateInverterData({ selected_inverters: updatedSelections }));
                              }}
                              className="w-11 px-1.5 py-0.5 text-xs 3xl:text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 focus:border-blue-300 disabled:bg-gray-50 disabled:text-gray-500 text-center"
                              disabled={!isModuleSelectorEnabled}
                            />
                            <button
                              onClick={() => {
                                const updatedSelections = (inverterData.selected_inverters || []).filter((_: any, idx: number) => idx !== index);
                                dispatch(updateInverterData({ selected_inverters: updatedSelections }));
                              }}
                              className="w-5 h-5 flex items-center justify-center text-red-600 hover:bg-red-100 rounded text-xs 3xl:text-sm font-medium"
                              disabled={!isModuleSelectorEnabled}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>


              {inverterData.selected_brand && (
                <div className="mt-4 space-y-3">
                  {inverterData.selected_brand.name?.toUpperCase() === INVERTER_BRANDS.SOLAREDGE && inverterData.selected_type === InverterType.INVERTER && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[11px] 3xl:text-xs uppercase tracking-[0.2em] text-slate-700 font-semibold mb-2">Optimizer</div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                            Optimizer
                          </label>
                          <SearchDropdown
                            options={optimizers?.map(opt => ({ id: opt.id, label: `${opt.type} (${opt.current}A)` })) || []}
                            onSelect={(option: any) => {
                              const optimizer = option ? optimizers?.find(opt => opt.id === option.id) : undefined;
                              handleOptimizerSelect(optimizer);
                            }}
                            placeholder="Search optimizers..."
                            isLoading={!is_initialized}
                            value={inverterData.selected_optimizer ? { id: inverterData.selected_optimizer.type, label: `${inverterData.selected_optimizer.type} (${inverterData.selected_optimizer.current}A)` } : null}
                            disabled={!isModuleSelectorEnabled}
                            inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="1000"
                            value={inverterData.optimizer_quantity || ''}
                            onChange={(e) => handleOptimizerQuantityChange(parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                            placeholder="0"
                            disabled={!isModuleSelectorEnabled}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {inverterData.selected_brand.name?.toUpperCase() === INVERTER_BRANDS.TESLA && inverterData.selected_type === InverterType.INVERTER && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[11px] 3xl:text-xs uppercase tracking-[0.2em] text-slate-700 font-semibold mb-2">RSD</div>
                      <div className="grid grid-cols-3 gap-3 items-start">
                        <div>
                          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                            RSD Type
                          </label>
                          <SearchDropdown
                            options={
                              rsds
                                .filter(r => {
                                  const brandId = inverterBrands.find(b => b.name === inverterData.selected_brand?.name)?.id;
                                  return brandId ? r.inverter_brand_id === brandId : false;
                                })
                                .map(r => ({ id: r.name, label: r.name }))
                            }
                            onSelect={(option) => handleRsdUpdate(option?.label || '', inverterData.rsd?.quantity || 0, inverterData.rsd?.current || '')}
                            placeholder="Choose RSD type..."
                            value={inverterData.rsd?.type ? { id: inverterData.rsd.type, label: inverterData.rsd.type } : null}
                            disabled={!isModuleSelectorEnabled}
                            className="text-xs 3xl:text-sm"
                            inputClassName="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                            RSD Quantity
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="1000"
                            value={inverterData.rsd?.quantity || ''}
                            onChange={(e) => handleRsdUpdate(inverterData.rsd?.type || '', parseInt(e.target.value) || 0, inverterData.rsd?.current || '')}
                            className="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                            placeholder="0"
                            disabled={!isModuleSelectorEnabled}
                          />
                        </div>
                        <div>
                          <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                            RSD Current
                          </label>
                          <input
                            type="text"
                            value={inverterData.rsd?.current || ''}
                            onChange={(e) => handleRsdUpdate(inverterData.rsd?.type || '', inverterData.rsd?.quantity || 0, e.target.value)}
                            className="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                            placeholder="Enter current"
                            disabled={!isModuleSelectorEnabled}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(inverterData.selected_brand.name?.toUpperCase() === INVERTER_BRANDS.ENPHASE || inverterData.selected_brand.name?.toUpperCase() === INVERTER_BRANDS.QCELLS) && inverterData.selected_type === InverterType.MICROINVERTER && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[11px] 3xl:text-xs uppercase tracking-[0.2em] text-slate-700 font-semibold mb-2">Combiner</div>
                      <SearchDropdown
                        options={
                          combiners
                            .filter(c => {
                              const brandId = inverterBrands.find(b => b.name === inverterData.selected_brand?.name)?.id;
                              return brandId ? c.inverter_brand_id === brandId : false;
                            })
                            .map(c => ({ id: c.name, label: c.name }))
                        }
                        onSelect={(option) => handleCombinerUpdate(option?.label || '')}
                        placeholder="Choose combiner..."
                        value={inverterData.combiner ? { id: inverterData.combiner, label: inverterData.combiner } : null}
                        disabled={!isModuleSelectorEnabled}
                        className="text-xs 3xl:text-sm"
                        inputClassName="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>

        {/* Batteries Section */}
        <div className="bg-white rounded-2xl border border-slate-200/80 border-l-[3px] border-l-blue-400 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-blue-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[11px] 3xl:text-xs font-semibold text-blue-700">3</span>
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h10a2 2 0 012 2v6a2 2 0 01-2 2H7a2 2 0 01-2-2V9a2 2 0 012-2zm10-3v2M7 4v2" />
              </svg>
              <div>
                <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Batteries</span>
                <span className="block text-[11px] 3xl:text-xs text-slate-500">Optional battery details for this system.</span>
              </div>
              </div>
            </div>
            <div className="p-5">

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                    Battery Brand
                  </label>
                  <SearchDropdown
                    options={batteryBrands?.map(brand => ({ id: brand.id, label: brand.name })) || []}
                    onSelect={(option: any) => {
                      const brand = option ? batteryBrands?.find(b => b.id === option.id) : undefined;
                      if (!brand) {
                        dispatch(updateBatteryData({
                          selected_brand: undefined,
                          selected_battery: undefined,
                          quantity: 0,
                          is_domestic: false
                        }));
                        dispatch(setCROutput({ generated_cr: {}, updated_cr: {} }));
                      } else {
                        dispatch(updateBatteryData({
                          selected_brand: brand.name,
                          selected_battery: undefined,
                          quantity: 0,
                          is_domestic: false
                        }));
                      }
                    }}
                    placeholder="Search brands..."
                    isLoading={!is_initialized}
                    value={batteryData.selected_brand ? { id: batteryData.selected_brand, label: batteryData.selected_brand } : null}
                    disabled={!isModuleSelectorEnabled}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`bat-brand-${activeSystem}`}
                  />
                </div>

                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                    Battery Model
                  </label>
                  <SearchDropdown
                    options={batteries?.map(battery => ({ id: battery.id, label: battery.batteryName })) || []}
                    onSelect={(option: any) => {
                      const battery = batteries?.find(b => b.id === option?.id);
                      if (battery) {
                        dispatch(updateBatteryData({
                          selected_battery: {
                            battery_name: battery.batteryName,
                            battery_model: battery.batteryModel,
                            kwh: battery.kwh,
                            current: battery.current,
                            wattage: battery.wattage
                          }
                        }));
                      } else {
                        dispatch(updateBatteryData({ selected_battery: undefined }));
                      }
                    }}
                    placeholder={batteryData.selected_brand ? "Search models..." : "Select brand first"}
                    isLoading={!is_initialized}
                    value={batteryData.selected_battery ? { id: batteryData.selected_battery.battery_name, label: batteryData.selected_battery.battery_name } : null}
                    disabled={!isModuleSelectorEnabled || !batteryData.selected_brand}
                    inputClassName="px-3 py-2 text-xs 3xl:text-sm rounded-md"
                    key={`bat-model-${activeSystem}`}
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={batteryData.quantity || ''}
                    onChange={(e) => dispatch(updateBatteryData({ quantity: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 text-xs 3xl:text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-200 focus:border-blue-300 disabled:bg-gray-50 disabled:text-gray-500 transition-all duration-200"
                    placeholder="0"
                    disabled={!isModuleSelectorEnabled}
                  />
                </div>

                <div>
                  <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                    Domestic
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={batteryData.is_domestic || false}
                      onChange={(e) => dispatch(updateBatteryData({ is_domestic: e.target.checked }))}
                      disabled={!isModuleSelectorEnabled}
                      className="w-4 h-4 text-emerald-600 focus:ring-blue-500 border-slate-300 rounded"
                    />
                    <span className="ml-2 text-xs 3xl:text-sm text-slate-700">Battery qualifies for domestic content</span>
                  </label>
                </div>
              </div>
            </div>
        </div>

        {/* Strings Section */}
        <div className="bg-white rounded-2xl border border-slate-200/80 border-l-[3px] border-l-blue-400 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 3xl:px-6 3xl:py-3.5 bg-blue-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-3">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[11px] 3xl:text-xs font-semibold text-blue-700">4</span>
                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16" />
                </svg>
                <div>
                  <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Strings</span>
                  <span className="block text-[11px] 3xl:text-xs text-slate-500">Add string labels and values for this system.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
              {Object.keys(stringsData).length < 10 && (
                <button
                  onClick={() => {
                    const existingNumbers = Object.keys(stringsData)
                      .filter(key => key.startsWith('string_'))
                      .map(key => parseInt(key.replace('string_', '')))
                      .filter(num => !isNaN(num))
                      .sort((a, b) => a - b);

                    let nextNumber = 1;
                    for (const num of existingNumbers) {
                      if (num === nextNumber) {
                        nextNumber++;
                      } else {
                        break;
                      }
                    }

                    const newKey = `string_${nextNumber}`;
                    const newStrings = { ...stringsData };
                    newStrings[newKey] = '';
                    dispatch(updateStringsData(newStrings));
                  }}
                  disabled={!isModuleSelectorEnabled}
                  className="px-2.5 py-1 text-[10px] 3xl:text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add String
                </button>
              )}
              </div>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(stringsData).map(([key, value]) => (
                  <div key={`${key}-${activeSystem}`} className="flex items-center gap-1">
                    <div className="flex-shrink-0">
                      <span className="text-xs 3xl:text-sm font-semibold text-slate-700 uppercase">{key}:</span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => {
                          const newStrings = { ...stringsData };
                          newStrings[key] = e.target.value;
                          dispatch(updateStringsData(newStrings));
                        }}
                        className="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                        placeholder="Enter string value"
                        disabled={!isModuleSelectorEnabled}
                      />
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => {
                          const newStrings = { ...stringsData };
                          delete newStrings[key];
                          dispatch(updateStringsData(newStrings));
                          dispatch(setCROutput({ generated_cr: {}, updated_cr: {} }));
                        }}
                        disabled={!isModuleSelectorEnabled}
                        className="w-7 h-7 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(stringsData).length >= 10 && (
                <p className="text-xs 3xl:text-sm text-rose-600 text-center py-2">
                  Maximum of 10 string configurations allowed
                </p>
              )}
            </div>
        </div>

        {/* Separator - Production is shared across all systems */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-slate-200"></div>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.2em] text-blue-600 font-medium">Shared across all systems</span>
          <div className="flex-1 h-px bg-slate-200"></div>
        </div>

        {/* Production Section (shared) */}
        <div className="bg-white rounded-2xl border border-slate-200/80 border-l-[3px] border-l-blue-400 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between gap-2 px-5 py-3 3xl:px-6 3xl:py-3.5 bg-blue-100/50 border-b border-slate-200/60">
              <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 19h16M4 15l4-4 4 4 4-4 4 4" />
              </svg>
              <div>
                <span className="block text-[11px] 3xl:text-xs uppercase tracking-widest font-bold text-slate-700">Production</span>
                <span className="block text-[11px] 3xl:text-xs text-slate-500">Applied globally after system-level inputs are complete.</span>
              </div>
              </div>
              <span className="rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[10px] 3xl:text-xs font-semibold uppercase tracking-wide text-blue-700">Shared</span>
            </div>
            <div className="p-5">
              <div>
                <label className="block text-xs 3xl:text-sm font-semibold text-slate-700 mb-1">
                  Production Value
                </label>
                <input
                  type="text"
                  value={productionData?.production_value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value.trim()) {
                      dispatch(setCROutput({ generated_cr: {}, updated_cr: {} }));
                    }
                    dispatch(updateProductionData({ production_value: value }));
                  }}
                  className="w-full px-3 py-2 text-xs 3xl:text-sm placeholder:text-[11px] 3xl:placeholder:text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  placeholder="Enter production value"
                  disabled={!isModuleSelectorEnabled}
                />
              </div>
            </div>
        </div>
        </div>
      </div>
    </div>
  );
};
